import { useState, useEffect } from 'react';
import { supabase } from '../../../../lib/supabaseClient';
import { BlogCategory } from '../../../../types/blog';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  category: BlogCategory | null;
  onSaved: () => void;
}

const PRESET_COLORS = [
  '#e07b54', '#5a9e6f', '#c4a047', '#7b7fce',
  '#c45e8a', '#4aa8c4', '#8a6e9e', '#c46b4a',
];

function toSlug(text: string): string {
  return text
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w-]/g, '')
    .replace(/--+/g, '-')
    .replace(/^-+|-+$/g, '');
}

export default function CategoryFormModal({ isOpen, onClose, category, onSaved }: Props) {
  const [name, setName] = useState('');
  const [slug, setSlug] = useState('');
  const [color, setColor] = useState(PRESET_COLORS[0]);
  const [slugManuallyEdited, setSlugManuallyEdited] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (isOpen) {
      if (category) {
        setName(category.name);
        setSlug(category.slug);
        setColor(category.color);
        setSlugManuallyEdited(true);
      } else {
        setName('');
        setSlug('');
        setColor(PRESET_COLORS[0]);
        setSlugManuallyEdited(false);
      }
      setError('');
    }
  }, [isOpen, category]);

  const handleNameChange = (val: string) => {
    setName(val);
    if (!slugManuallyEdited) {
      setSlug(toSlug(val));
    }
  };

  const handleSlugChange = (val: string) => {
    setSlug(val);
    setSlugManuallyEdited(true);
  };

  const handleSubmit = async () => {
    if (!name.trim()) { setError('カテゴリー名を入力してください。'); return; }
    if (!slug.trim()) { setError('スラッグを入力してください。'); return; }
    setSaving(true);
    setError('');

    const payload = {
      name: name.trim(),
      slug: slug.trim(),
      color,
    };

    let queryError;
    if (category) {
      const { error: err } = await supabase
        .from('8a4f04ed_blog_categories')
        .update(payload)
        .eq('id', category.id);
      queryError = err;
    } else {
      const { error: err } = await supabase
        .from('8a4f04ed_blog_categories')
        .insert(payload);
      queryError = err;
    }

    setSaving(false);
    if (queryError) {
      setError(queryError.message.includes('duplicate') ? 'このスラッグは既に使用されています。' : '保存に失敗しました。');
    } else {
      onSaved();
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      style={{ background: 'rgba(60,40,30,0.45)' }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        className="w-full max-w-md rounded-2xl overflow-hidden"
        style={{ background: '#fff', border: '1px solid #e8d8cc' }}
      >
        {/* Header */}
        <div
          className="flex items-center justify-between px-6 py-4"
          style={{ borderBottom: '2px solid #5a9e6f', background: '#f9f5f2' }}
        >
          <h2 className="font-bold text-base" style={{ color: '#4a3728' }}>
            {category ? 'カテゴリーを編集' : '新規カテゴリー追加'}
          </h2>
          <button
            onClick={onClose}
            className="flex items-center justify-center w-8 h-8 rounded-full transition-colors hover:bg-[#f0e8e0] cursor-pointer"
          >
            <i className="ri-close-line text-lg" style={{ color: '#9b8b7f' }}></i>
          </button>
        </div>

        {/* Body */}
        <div className="px-6 py-6 space-y-5">
          {/* Name */}
          <div>
            <label className="block text-sm font-bold mb-1.5" style={{ color: '#6b5a4e' }}>
              カテゴリー名 <span style={{ color: '#cc4444' }}>*</span>
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => handleNameChange(e.target.value)}
              placeholder="例：結婚準備、お役立ち情報"
              className="w-full px-4 py-2.5 rounded-lg text-sm border outline-none transition-colors"
              style={{
                border: '1.5px solid #ddd0c8',
                color: '#4a3728',
                background: '#fffaf8',
              }}
            />
          </div>

          {/* Slug */}
          <div>
            <label className="block text-sm font-bold mb-1.5" style={{ color: '#6b5a4e' }}>
              スラッグ <span style={{ color: '#cc4444' }}>*</span>
            </label>
            <input
              type="text"
              value={slug}
              onChange={(e) => handleSlugChange(e.target.value)}
              placeholder="例：wedding-preparation"
              className="w-full px-4 py-2.5 rounded-lg text-sm border outline-none transition-colors"
              style={{
                border: '1.5px solid #ddd0c8',
                color: '#4a3728',
                background: '#fffaf8',
                fontFamily: 'monospace',
              }}
            />
            <p className="text-xs mt-1" style={{ color: '#b0a09a' }}>
              URLに使われる英数字・ハイフン（小文字推奨）
            </p>
          </div>

          {/* Color */}
          <div>
            <label className="block text-sm font-bold mb-2" style={{ color: '#6b5a4e' }}>
              カラー
            </label>
            <div className="flex items-center gap-2 flex-wrap">
              {PRESET_COLORS.map((c) => (
                <button
                  key={c}
                  onClick={() => setColor(c)}
                  className="w-8 h-8 rounded-full border-2 transition-transform hover:scale-110 cursor-pointer"
                  style={{
                    background: c,
                    borderColor: color === c ? '#4a3728' : 'transparent',
                    boxShadow: color === c ? '0 0 0 2px #fff inset' : 'none',
                  }}
                />
              ))}
              <div
                className="flex items-center gap-1.5 px-3 py-1 rounded-lg cursor-pointer"
                style={{ border: '1.5px solid #ddd0c8', background: '#fffaf8' }}
              >
                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-6 h-6 rounded cursor-pointer border-0 bg-transparent"
                />
                <span className="text-xs font-mono" style={{ color: '#9b8b7f' }}>{color}</span>
              </div>
            </div>

            {/* Preview */}
            <div className="mt-3 flex items-center gap-2">
              <span className="text-xs font-bold" style={{ color: '#9b8b7f' }}>プレビュー：</span>
              <span
                className="inline-block px-3 py-1 rounded-full text-xs font-bold text-white"
                style={{ background: color }}
              >
                {name || 'カテゴリー名'}
              </span>
            </div>
          </div>

          {error && (
            <div
              className="px-4 py-2.5 rounded-lg text-sm"
              style={{ background: '#fff0f0', color: '#cc4444', border: '1px solid #f8c4c4' }}
            >
              {error}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-end gap-3 px-6 py-4"
          style={{ borderTop: '1px solid #e8d8cc', background: '#f9f5f2' }}
        >
          <button
            onClick={onClose}
            className="px-5 py-2 rounded-lg text-sm font-bold transition-opacity hover:opacity-80 cursor-pointer whitespace-nowrap"
            style={{ background: '#f0e8e0', color: '#6b5a4e' }}
          >
            キャンセル
          </button>
          <button
            onClick={handleSubmit}
            disabled={saving}
            className="px-5 py-2 rounded-lg text-sm font-bold text-white transition-opacity hover:opacity-90 cursor-pointer whitespace-nowrap disabled:opacity-60"
            style={{ background: '#5a9e6f' }}
          >
            {saving ? '保存中...' : category ? '更新する' : '追加する'}
          </button>
        </div>
      </div>
    </div>
  );
}
